import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sun, Moon, Camera, Undo2, Redo2, Save, Share2, Settings2,
  Home, Sparkles, Ruler, History, Hand
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore, useAppStore, useProjectsStore } from '../store';
import { T } from '../i18n';
import ObjectBar from '../components/ObjectBar';
import ParamsPanel from '../components/ParamsPanel';
import CanvasRenderer from '../components/CanvasRenderer';
import SaveModal from '../components/SaveModal';
import AuthModal from '../components/AuthModal';
import MeasureOverlay from '../components/MeasureOverlay';
import AIHistoryPanel from '../components/AIHistoryPanel';
import ShareModal from '../components/ShareModal';
import { extractBase64FromDataUrl, readImageFileAsDataUrl } from '../utils/photoImport';
import { requestAiGeneration } from '../utils/requestAiGeneration';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

export default function ResultPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, updateCredits } = useAuthStore();
  const { 
    lang, photo, photoBase64, photoWithMeasure, currentObject, setCurrentObject,
    iaImage, setIaImage, iaLoading, setIaLoading,
    undoIa, redoIa, iaStack, iaIndex,
    weatherMode, toggleWeather, configs, measure
  } = useAppStore();
  const t = T[lang] || T.fr;
  const [savedProjectId, setSavedProjectId] = useState(null);
  const currentProjectId = savedProjectId || location.state?.projectId || `session_${Date.now()}`;
  
  const [showParams, setShowParams] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showMeasure, setShowMeasure] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [aiProvider, setAiProvider] = useState('openrouter');
  const fileInputRef = useRef(null);

  // Show measure overlay when photo is loaded and no measure yet
  useEffect(() => {
    if (photo && !measure.hasMeasure && !measure.active) {
      setShowMeasure(true);
      useAppStore.getState().setMeasure({ active: true });
    }
  }, [photo, measure.hasMeasure, measure.active]);

  // Non-production debug hook for deterministic E2E runs.
  // Example: /result?debug_measure_cm=300&debug_measure_px=1179
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!photo) return;

    const params = new URLSearchParams(location.search);
    const cm = Number(params.get('debug_measure_cm'));
    const px = Number(params.get('debug_measure_px'));

    if (!Number.isFinite(cm) || !Number.isFinite(px)) return;
    if (cm <= 0 || px <= 0) return;

    useAppStore.getState().setMeasure({
      active: false,
      taps: [],
      pixDist: px,
      cmVal: cm,
      scale: px / cm,
      hasMeasure: true,
    });
    setShowMeasure(false);
  }, [location.search, photo]);
  
  // Show object selection hint if no object selected
  const showObjectHint = !currentObject && !iaImage && !showMeasure;
  
  const handleGenerateAI = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    
    if (user.credits <= 0) {
      toast.error(t.no_credits);
      return;
    }
    
    if (!currentObject) {
      toast.error(t.choose_object);
      return;
    }
    
    setIaLoading(true);
    
    try {
      const data = await requestAiGeneration({
        apiUrl: API_URL,
        token,
        payload: {
          photo_base64: photoWithMeasure || photoBase64 || '',
          original_photo_base64: photoBase64 || '',
          measured_photo_base64: photoWithMeasure || photoBase64 || '',
          object_type: currentObject,
          config: configs[currentObject],
          provider: aiProvider,
        },
      });
      
      if (data.image_url) {
        setIaImage(data.image_url);
        updateCredits(data.credits_remaining);
        toast.success('Image générée !');
      } else {
        toast.info('Simulation en cours...');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIaLoading(false);
    }
  };
  
  const handleSave = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    setShowSave(true);
  };
  
  const handleShare = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    if (!iaImage) {
      toast.error('Générez d\'abord une simulation');
      return;
    }
    setShowShare(true);
  };
  
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await readImageFileAsDataUrl(file);
      const base64 = extractBase64FromDataUrl(dataUrl);
      useAppStore.getState().setPhoto(URL.createObjectURL(file), base64);
    } finally {
      e.target.value = '';
    }
  };
  
  return (
    <div className="h-full flex flex-col relative">
      {/* Hidden input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePhotoChange}
      />
      
      {/* Header */}
      <header className="h-14 glass flex items-center justify-between px-2 sm:px-4 flex-shrink-0 z-30">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/home')}
            className="p-2 rounded-lg hover:bg-white/5"
            data-testid="back-btn"
          >
            <Home size={20} className="text-muted-foreground" />
          </button>
        </div>
        
        <div className="font-display text-center leading-none">
          <span className="text-secondary font-bold text-sm">SoView</span>
          <br />
          <span className="text-primary font-bold text-sm">FX</span>
        </div>
        
        <div className="flex items-center gap-1 sm:gap-2 max-w-[55%] sm:max-w-none">
          {user && (
            <button
              onClick={() => setShowHistory(true)}
              className="p-2 rounded-lg hover:bg-white/5"
              data-testid="history-btn"
              title={t.ai_history || 'Historique IA'}
            >
              <History size={20} className="text-muted-foreground" />
            </button>
          )}
          {currentObject && (
            <div className="flex items-center gap-0 sm:gap-1">
              <select
                id="ai-provider"
                name="aiProvider"
                value={aiProvider}
                onChange={(e) => setAiProvider(e.target.value)}
                className="bg-white/10 text-white text-[10px] px-1.5 sm:px-2 py-1 rounded-l-full border-none outline-none cursor-pointer max-w-[65px] sm:max-w-none"
                data-testid="ai-provider-select"
              >
                <option value="gemini">Gemini (natif)</option>
                <option value="openai">OpenAI</option>
                <option value="openrouter">OpenRouter</option>
              </select>
              <button
                onClick={handleGenerateAI}
                disabled={iaLoading}
                className="flex items-center gap-1 bg-secondary text-white px-3 py-1.5 rounded-r-full text-xs font-bold disabled:opacity-50"
                data-testid="generate-ai-btn"
              >
                <Sparkles size={14} />
                {iaLoading ? '...' : 'IA'}
              </button>
            </div>
          )}
          <button
            onClick={toggleWeather}
            className="p-2 rounded-lg hover:bg-white/5"
            data-testid="weather-btn"
          >
            {weatherMode === 'day' ? (
              <Sun size={20} className="text-primary" />
            ) : (
              <Moon size={20} className="text-blue-400" />
            )}
          </button>
        </div>
      </header>
      
      {/* Canvas Zone */}
      <div className="flex-1 relative overflow-hidden bg-background-tertiary">
        <CanvasRenderer />
        
        {/* Measure Overlay */}
        <AnimatePresence>
          {showMeasure && photo && (
            <MeasureOverlay 
              onComplete={(cm, scale) => {
                setShowMeasure(false);
                toast.success(`Échelle: ${scale.toFixed(2)} px/cm`);
              }}
              onSkip={() => {
                setShowMeasure(false);
                toast.info(t.no_measure_warning);
              }}
            />
          )}
        </AnimatePresence>
        
        {/* Object selection hint */}
        <AnimatePresence>
          {showObjectHint && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="absolute bottom-20 left-0 right-0 flex justify-center pointer-events-none"
            >
              <div className="bg-black/60 border border-secondary rounded-2xl px-6 py-4 text-center backdrop-blur-sm">
                <p className="flex justify-center mb-2"><Hand size={24} className="text-white" /></p>
                <p className="text-white font-bold">{t.choose_object}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Photo button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="absolute bottom-4 right-4 w-12 h-12 bg-secondary rounded-full flex items-center justify-center shadow-lg touch-feedback z-10"
          data-testid="change-photo-btn"
        >
          <Camera size={22} className="text-white" />
        </button>
        
        {/* AI Loading Overlay */}
        <AnimatePresence>
          {iaLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 flex items-center justify-center z-20"
            >
              <div className="glass-light rounded-2xl p-6 flex flex-col items-center gap-4 max-w-[260px]">
                <div className="spinner" />
                <p className="text-white font-bold text-sm text-center">{t.ia_loading}</p>
                <p className="text-muted-foreground text-xs">{t.ia_loading_wait}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Object Bar */}
      <ObjectBar 
        onSelect={(obj) => {
          useAppStore.getState().resetIa();
          setCurrentObject(obj);
          setShowParams(true);
        }}
      />
      
      {/* Footer */}
      <div className="footer-action-bar">
        <button
          onClick={undoIa}
          disabled={iaIndex <= 0}
          className="flex flex-col items-center gap-1 px-2 sm:px-3 py-1 rounded-lg disabled:opacity-30 footer-btn"
          data-testid="undo-btn"
        >
          <Undo2 size={18} />
          <span className="text-[10px] text-muted-foreground">Undo</span>
        </button>
        
        <button
          onClick={() => {
            if (photo) {
              useAppStore.getState().resetMeasure();
              setShowMeasure(true);
              useAppStore.getState().setMeasure({ active: true });
            } else {
              toast.error('Importez une photo pour calibrer l\'échelle');
            }
          }}
          className={`flex flex-col items-center gap-1 px-2 sm:px-3 py-1 rounded-lg ${measure.hasMeasure ? 'text-green-400' : ''} footer-btn`}
          data-testid="measure-btn"
        >
          <Ruler size={18} className={measure.hasMeasure ? 'text-green-400' : ''} />
          <span className="text-[10px] text-muted-foreground">
            {measure.hasMeasure ? `${measure.cmVal}cm` : 'Mesure'}
          </span>
        </button>
        
        <button
          onClick={() => setShowParams(true)}
          className="flex flex-col items-center gap-1 px-2 sm:px-3 py-1 rounded-lg footer-btn"
          data-testid="params-btn"
        >
          <Settings2 size={18} className="text-secondary" />
          <span className="text-[10px] text-muted-foreground">{t.settings}</span>
        </button>
        
        <button
          onClick={redoIa}
          disabled={iaIndex >= iaStack.length - 1}
          className="flex flex-col items-center gap-1 px-2 sm:px-3 py-1 rounded-lg disabled:opacity-30 footer-btn"
          data-testid="redo-btn"
        >
          <Redo2 size={18} />
          <span className="text-[10px] text-muted-foreground">Redo</span>
        </button>
        
        <button
          onClick={handleSave}
          className="flex flex-col items-center gap-1 px-2 sm:px-3 py-1 rounded-lg footer-btn"
          data-testid="save-btn"
        >
          <Save size={18} className="text-primary" />
          <span className="text-[10px] text-muted-foreground">{t.save}</span>
        </button>

        <button
          onClick={handleShare}
          className="flex flex-col items-center gap-1 px-2 sm:px-3 py-1 rounded-lg footer-btn"
          data-testid="share-btn"
        >
          <Share2 size={18} />
          <span className="text-[10px] text-muted-foreground">{t.share}</span>
        </button>
      </div>
      
      {/* Params Panel */}
      <AnimatePresence>
        {showParams && currentObject && (
          <ParamsPanel 
            objectType={currentObject}
            onClose={() => setShowParams(false)}
            hasPhoto={!!photoBase64}
            onValidate={() => {
              setShowParams(false);
              if (!photoBase64) {
                toast.error('Veuillez d\'abord importer une photo pour générer une simulation IA');
                return;
              }
              handleGenerateAI();
            }}
          />
        )}
      </AnimatePresence>
      
      {/* Save Modal */}
      {showSave && (
        <SaveModal
          onClose={() => setShowSave(false)}
          onSaved={(projectId) => setSavedProjectId(projectId)}
        />
      )}
      
      {/* Auth Modal */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      
      {/* AI History Modal */}
      <AnimatePresence>
        {showHistory && (
          <AIHistoryPanel 
            onClose={() => setShowHistory(false)}
            onSelectImage={(imageUrl) => {
              setIaImage(imageUrl);
              toast.success(t.image_loaded || 'Image chargée');
            }}
          />
        )}
      </AnimatePresence>

      {/* Share Modal */}
      {showShare && (
        <ShareModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          projectId={currentProjectId}
          simulationId={`sim_${Date.now()}`}
          projectName={`${currentObject ? currentObject.charAt(0).toUpperCase() + currentObject.slice(1) : 'Simulation'} - ${new Date().toLocaleDateString('fr-FR')}`}
          simulatedImageUrl={iaImage || ''}
          originalImageUrl={photo || undefined}
          simulationParameters={currentObject ? configs[currentObject] || {} : {}}
          createdAt={new Date().toISOString()}
        />
      )}

    </div>
  );
}
